import React, { useState, useEffect } from 'react';
import MHeader from '@/components/ui/organisms/MHeader';
import { getMemberInfo } from '@/hooks/useMembers';
import { useMemberPermission } from '@/hooks/useMemberPermission';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFamily } from '@/hooks/useFamily';
import { relationshipOptions } from '@/types/relationship';
import MCheckbox from '@/components/ui/atoms/MCheckbox';
import { MButton } from '@/components/ui/atoms/MButtons';
import MDialog from '@/components/ui/atoms/MDialog';
import MemberCard from '../MemberCard';
import MLoader from '@/components/ui/atoms/MLoader';
import { membersService } from '@/services/members.service';
import { isChild } from '@/types/member';
import MSpacer from '@/components/ui/atoms/MSpacer';
import MFrame from '@/components/ui/atoms/MFrame';
import { IconUserX, IconInfoCircle } from '@tabler/icons-react';
import FooterDrawer from '@/components/ui/atoms/FooterDrawer';
import { useToast } from '@/components/ui/atoms/MToastProvider';
import { memberPermissionsService } from '@/services/memberPermissions.service';
import { usePendingAccessRequests } from '@/hooks/usePendingAccessRequests';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

function getRelationshipLabel(relationship: string | number | undefined) {
  if (typeof relationship === 'number') {
    const rel = relationshipOptions.find(opt => opt.value === relationship);
    return rel ? rel.label : '';
  } else if (typeof relationship === 'string') {
    return relationship;
  }
  return '';
}

const MemberCanAccessProfiles: React.FC = () => {
  const memberInfo = getMemberInfo(true);
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedMember = location.state?.selectedMember;
  const { data: familyMembers = [], managedIds } = useFamily();
  const [localAccess, setLocalAccess] = useState<Array<{ member: any; hasAccess: boolean; consentPending?: boolean; checkedAccess?: boolean }>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState<any[]>([]);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [selectedMemberForAccessRequest, setSelectedMemberForAccessRequest] = useState<any>(null);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const { pendingRequests } = usePendingAccessRequests({
    familyMembers,
    managedIds,
    familyMemberType: memberInfo.family_member_type,
    enabled: memberInfo.family_member_type === 'fm' // Only enable for Family Members
  });
  const [consentPendingInfoDrawerOpen, setConsentPendingInfoDrawerOpen] = useState(false);

  // Determine if this is viewing own profile or managing another's profile
  const isViewingOwnProfile = selectedMember?.id === memberInfo.id;
  const isLoggedInUserFO = memberInfo.family_member_type === 'fo';

  // Track GTM user_data_loaded event on page load
  useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_MEMBER_MANAGE,
    });
  }, [memberInfo?.id]);

  useEffect(() => {
    async function fetchAccessList() {
      if (!selectedMember || familyMembers.length === 0) return;
      
      setLoading(true);
      try {
        // Fetch which profiles the SELECTED member can access using correct endpoint
        const managedMembers = await membersService.getManagedMembersForMember(selectedMember.id);
        const managedIds = managedMembers.map((m: any) => m.id || m._id);
        
        // Fetch tokens where selectedMember is requesting access to other profiles
        // Uses new endpoint that queries for token_params.familyMemberId = selectedMember.id
        const { tokens } = await memberPermissionsService.getPendingAccessTokensForMember(selectedMember.id);
        setTokens(tokens || []);
        
        // Note: Using managedIds from useFamily hook instead of fetching separately
        // This prevents multiple API calls and provides stable data
  
        setLocalAccess(
          familyMembers
            .filter(m => m.id !== selectedMember.id)
            .map(m => {
              // Check if there's a pending token where m is requesting access to selectedMember's profile
              // token.member_id = selectedMember (profile owner)
              // token.token_params.familyMemberId = m (person requesting access)
              const consentPending = tokens?.some(t => t.token_params?.familyMemberId === m.id);
              const hasAccess = managedIds.includes(m.id);
              
              return {
                member: m,
                hasAccess,
                consentPending,
                checkedAccess: hasAccess || consentPending,
              };
            })
        );

        // Note: pending requests are now handled by usePendingAccessRequests hook
        // The hook automatically checks all family members and updates state
      } finally {
        setLoading(false);
      }
    }
  
    fetchAccessList();
  }, [selectedMember?.id, familyMembers, memberInfo.id, isLoggedInUserFO, isViewingOwnProfile]);  

  const { handleGrantAccess } = useMemberPermission(selectedMember?.id, selectedMember?.current_family);

  const handleToggle = (memberId: string) => {
    setLocalAccess((prev) =>
      prev.map((item) =>
        item.member.id === memberId ? { ...item, checkedAccess: !item.checkedAccess } : item
      )
    );
  };

  const [confirmChecked, setConfirmChecked] = useState(false);

  const handleRequestAccess = (member: any) => {
    setSelectedMemberForAccessRequest(member);
    setShowConsentDialog(true);
  };

  const handleConfirmRequestAccess = async () => {
    if (!selectedMemberForAccessRequest) return;
    
    setRequestingAccess(true);
    try {
      await memberPermissionsService.requestAccess({
        memberId: memberInfo.id,
        familyId: selectedMember.current_family,
        familyMemberId: selectedMemberForAccessRequest.id,
      });
      toast.showToast({ message: 'Access request sent to Family Owner', variant: 'default' });
      setShowConsentDialog(false);
      setSelectedMemberForAccessRequest(null);
      
      // Reload page to update button state
      globalThis.window?.location.reload();
    } catch (error) {
      toast.showToast({ message: 'Failed to send access request', variant: 'error' });
      setRequestingAccess(false);
    }
  };

  const handleDone = async () => {
    setSubmitting(true);
    
    const revokePromises = localAccess
      .filter(item => {
        const consentPending = tokens.some(t => t.token_params?.familyMemberId === item.member.id);
        const wasChecked = item.hasAccess || consentPending;
        const nowChecked = !!item.checkedAccess;
        return wasChecked && !nowChecked;
      })
      .map(item => {
        const consentPending = tokens.some(t => t.token_params?.familyMemberId === item.member.id);
        if (consentPending) {
          const tokenObj = tokens.find(t => t.token_params?.familyMemberId === item.member.id);
          return memberPermissionsService.revokeInvite({
            memberId: selectedMember.id,        // Profile owner (token.member_id)
            familyId: selectedMember.current_family,
            familyMemberId: item.member.id,     // Person requesting access (token.token_params.familyMemberId)
            token: tokenObj?.token,
          });
        } else if (item.hasAccess) {
          return memberPermissionsService.revokeAccess({
            memberId: selectedMember.id,        // Member whose access is being revoked
            familyId: selectedMember.current_family,
            familyMemberId: item.member.id,     // Profile they have access to
          });
        }
        return Promise.resolve();
      });
      
    const grantedMembers = localAccess.filter(item => {
      const wasChecked = item.hasAccess;
      const nowChecked = !!item.checkedAccess;
      return !wasChecked && nowChecked;
    });
    
    const grantPromises = grantedMembers.map(item => {
      // Check if the member being granted access is a pet
      const skipConsent = item.member.member_type === 'p';
      // memberId = person granting access (item.member.id)
      // familyMemberId = person receiving access (selectedMember.id)
      return handleGrantAccess(item.member.id, selectedMember.id, skipConsent);
    });
      
    // Check if any granted members require consent (i.e., are not pets)
    const hasInvites = grantedMembers.some(item => item.member.member_type !== 'p') && grantPromises.length > 0;
    const hasRevokes = revokePromises.length > 0;
    
    try {
      await Promise.all([...revokePromises, ...grantPromises]);
      if (hasInvites) {
        toast.showToast({ message: 'Access grant notification sent', variant: 'default' });
      } else if (hasRevokes) {
        toast.showToast({ message: 'Access removal notification sent', variant: 'default' });
      } else {
        toast.showToast({ message: 'Access grant notification sent', variant: 'default' });
      }
      navigate(-1);
    } catch {
      toast.showToast({ message: 'Please try again.', variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const hasChanges = localAccess.some(item => {
    const consentPending = item.consentPending;
    const wasChecked = item.hasAccess || consentPending;
    const nowChecked = item.checkedAccess;
    return wasChecked !== nowChecked;
  });

  if (loading || submitting || !selectedMember) {
    return <MLoader />;
  }

  const memberName = selectedMember.nickname || selectedMember.first_name;

  // Determine title and description based on 4 states
  let title = '';
  let description = '';
  let learnMoreContent = '';

  if (isViewingOwnProfile) {
    // State 1 & 2: Viewing own profile
    title = 'Profiles you can access';
    if (isLoggedInUserFO) {
      // State 1: FO viewing own profile
      description = 'As Family Owner, you have access to all members of your family.';
      learnMoreContent = 'The Family Owner has access to all family member profiles by default. This cannot be changed.';
    } else {
      // State 2: FM viewing own profile
      description = 'View which family member profiles you can access. Learn More';
      learnMoreContent = 'This shows the family member profiles you can currently view. You can ask the Family Owner if you want to change which profiles you can access.';
    }
  } else {
    // State 3 & 4: Managing another member's profile
    title = `Profiles ${memberName} can access`;
    if (!isLoggedInUserFO && memberInfo.family_member_type === 'fm') {
      // FM viewing another member's profile (view-only)
      description = 'View which family member profiles they can access.';
      learnMoreContent = 'This shows the family member profiles they can currently view.\n\nIf you\'d like to change access, you can ask the Family Owner.';
    } else if (isLoggedInUserFO && selectedMember.family_member_type === 'fm') {
      // FO viewing FM's profile
      description = 'Choose which family member profiles they can access';
      learnMoreContent = 'As a Family Owner, you decide which family members see another member\'s health information. Only members who have a Maatra account can be given access.\n\nGiving access allows them to view or update health records.';
    } else {
      // Other cases
      description = 'Choose which family member profiles they can access';
      learnMoreContent = 'Granting access allows the selected member to view, add, edit, and delete health records in Maatra. Only registered family members can be given access.';
    }
  }

  return (
    <div className="profile-page-root min-h-screen flex flex-col">
      <MHeader
        module="Family"
        memberInfo={memberInfo}
        title={title}
        description={description}
        learnMoreContent={learnMoreContent}
        hideBack={true}
      />
      <MSpacer mt={32} />
      <div className="flex flex-col gap-3">
        {localAccess.length === 0 && familyMembers.filter((m: any) => m.registered_member === true).length === 1 && (
          <MFrame>
            <div className="mframeInner">
              <IconUserX size={64} color="#AD95D1" style={{ marginBottom: 15 }} />
              <div className="body-base-semibold color-text-indigo-600" style={{ marginBottom: 15 }}>
                No profiles to access yet
              </div>
              <div className="accessRequestEmptyText">
                No other family members are registered yet. Invite family members to join Maatra.
              </div>
            </div>
          </MFrame>
        )}
        {[...localAccess]
          .filter(item => item.member.id !== selectedMember.id)
          .sort((a, b) => {
            if (a.member.family_member_type === 'fo') return -1;
            if (b.member.family_member_type === 'fo') return 1;
            return 0;
          })
          .map((item) => {
            const consentPending = item.consentPending;
            
            // State 1: FO viewing own profile - simple list without checkboxes or status labels
            if (isLoggedInUserFO && isViewingOwnProfile) {
              return (
                <MemberCard
                  key={item.member.id}
                  name={`${item.member.nickname || item.member.first_name}`.trim()}
                  isOwner={item.member.family_member_type === 'fo'}
                  relationship={getRelationshipLabel(item.member.relationship)}
                  member_type={item.member.family_member_type}
                  className="cursor-pointer"
                  registered={item.member.registered_member}
                  invited={item.member.invited}
                  showStatusLabels={false}
                  hideViewProfileLink={true}
                />
              );
            }
            
            // For all other states, show status labels and checkboxes
            let accessStatus: string | undefined = undefined;
            let accessStatusClass = '';

            if (consentPending) {
              accessStatus = (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>Consent Pending</span>
                  <IconInfoCircle 
                    size={16} 
                    color="#407ED0"
                    style={{ cursor: 'pointer' }} 
                    onClick={(e) => {
                      e.stopPropagation();
                      setConsentPendingInfoDrawerOpen(true);
                    }}
                  />
                </div>
              ) as any;
              accessStatusClass = '';
            } else if (item.hasAccess) {
              accessStatus = 'Access provided';
              accessStatusClass = 'statusLabelRegistered';
            } else {
              accessStatus = 'No access';
              accessStatusClass = 'accessStatusNoAccess';
            }

            // Determine if checkbox should be disabled
            // For FM managing other profiles: disable if FM doesn't have access to that member
            // Also disable all rows if FM is trying to manage FO's access
            // Also disable all checkboxes if FM is viewing another member's profile (not their own)
            const isFMManagingFO = !isLoggedInUserFO && selectedMember.family_member_type === 'fo';
            const isFMViewingAnotherMember = !isLoggedInUserFO && !isViewingOwnProfile && memberInfo.family_member_type === 'fm';
            const shouldDisableCheckbox = 
              loading ||
              isFMManagingFO ||
              isFMViewingAnotherMember ||
              (!isLoggedInUserFO && !isViewingOwnProfile && !managedIds.includes(item.member.id));

            return (
              <div 
                key={item.member.id}
                style={shouldDisableCheckbox ? { pointerEvents: 'none' } : undefined}
              >
                <MemberCard
                  name={`${item.member.nickname || item.member.first_name}`.trim()}
                  isOwner={item.member.family_member_type === 'fo'}
                  relationship={getRelationshipLabel(item.member.relationship)}
                  member_type={item.member.family_member_type}
                  className="cursor-pointer"
                  menu={
                    isViewingOwnProfile && !isLoggedInUserFO && !item.hasAccess && !consentPending ? (
                      <MButton
                        size="sm"
                        variant="outline"
                        disabled={pendingRequests[item.member.id]}
                        onClick={() => { handleRequestAccess(item.member); }}
                      >
                        {pendingRequests[item.member.id] ? 'Access Requested' : 'Ask for access'}
                      </MButton>
                    ) : (
                      <MCheckbox
                        checked={item.checkedAccess}
                        disabled={shouldDisableCheckbox}
                        onChange={() => handleToggle(item.member.id)}
                      />
                    )
                  }
                  registered={item.member.registered_member}
                  invited={item.member.invited}
                  accessStatus={accessStatus}
                  accessStatusClass={accessStatusClass}
                  showStatusLabels={true}
                />
              </div>
            );
          })}
      </div>
      {selectedMember.member_type === 'h' && isChild({ dob_year: selectedMember.dob_year, dob_month: selectedMember.dob_month, dob_day: selectedMember.dob_day }) && familyMembers.filter(m => m.registered_member).length > 1 && (
        <div className="flex flex-row items-center gap-2 mt-8 mb-6 px-2">
          <MCheckbox
            checked={confirmChecked}
            onChange={() => setConfirmChecked(v => !v)}
            id="confirm-checkbox"
          />
          <label htmlFor="confirm-checkbox" className="body-base-medium default-color" style={{cursor: 'pointer', marginLeft: 16}}>
            I confirm that the selected adults are the child's parents or legal guardians.
          </label>
        </div>
      )}
      {((!isViewingOwnProfile || (isViewingOwnProfile && !isLoggedInUserFO)) && familyMembers.filter(m => m.registered_member).length > 1) && (
        <div className="confirm-footer">
          <MButton
            size="lg"
            className="w-full"
            onClick={handleDone}
            disabled={
              submitting ||
              !hasChanges ||
              (
                selectedMember.member_type === 'h' &&
                isChild({
                  dob_year: selectedMember.dob_year,
                  dob_month: selectedMember.dob_month,
                  dob_day: selectedMember.dob_day
                }) &&
                !confirmChecked
              )
            }
          >
            Done
          </MButton>
        </div>
      )}
      
      {/* Consent Confirmation Dialog */}
      <MDialog
        isOpen={showConsentDialog}
        onClose={() => {
          setShowConsentDialog(false);
          setSelectedMemberForAccessRequest(null);
        }}
        title="Consent needed"
        description="Please confirm that you have this family member's consent to view their health information."
        primaryCtaText="Confirm"
        secondaryCtaText="Cancel"
        onPrimaryCta={handleConfirmRequestAccess}
        onSecondaryCta={() => {
          setShowConsentDialog(false);
          setSelectedMemberForAccessRequest(null);
        }}
        isPrimaryCtaLoading={requestingAccess}
      />

      {/* Consent Pending Info Drawer */}
      <FooterDrawer
        open={consentPendingInfoDrawerOpen}
        onClose={() => {
          setConsentPendingInfoDrawerOpen(false);
        }}
        title="Consent Pending"
      >
        <div className="accessRequestText">
          {isLoggedInUserFO && selectedMember.family_member_type === 'fm'
            ? (<>
                Notification sent to {selectedMember?.nickname || selectedMember?.first_name} to access this member's profile.
                <br /><br />
                They'll be able to access the profile once they give consent.
              </>)
            : (<>
                Notification sent to this family member to access the profile.
                <br /><br />
                They'll be able to access the profile once they give consent.
              </>)
          }
        </div>
      </FooterDrawer>
    </div>
  );
};

export default MemberCanAccessProfiles;
